import React, { createContext, useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import storage from 'shared/storage';

const SignupContext = createContext();

const STORAGE_KEY = 'promoter-signup-data';

const initialFormData = {
  // Phone verification
  phone: '',
  calling_code: '1',
  country_code: 'US',
  verification_code: '',

  // User info (conditional - for new users only)
  first_name: '',
  last_name: '',
  email: '',
  zipcode: '',
  user_role: '',

  // Business details - Account Owner & Payouts
  is_primary_owner: true,
  account_owner: {
    first_name: '',
    last_name: '',
    email: '',
    cellphone: '',
    calling_code: '1',
    country_code: 'US',
  },
  has_separate_payouts_contact: false,
  payouts_contact: {
    first_name: '',
    last_name: '',
    email: '',
    cellphone: '',
    calling_code: '1',
    country_code: 'US',
  },

  // Business details
  promoter_type: '',
  business_dba: '',
  legal_name: '',
  primary_contact: '',
  business_email: '',
  title: '',
  currency: '',
  business_phone: '',
  business_calling_code: '1',
  business_country_code: 'US',

  // Track address
  track_address: {
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipcode: '',
    country: 'US',
  },

  // Shipping address
  shipping_address: {
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipcode: '',
    country: 'US',
    same_as_track: false,
  },

  // Mailing address
  mailing_address: {
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipcode: '',
    country: 'US',
    same_as_track: false,
    same_as_shipping: false,
  },

  // Flow control
  is_existing_user: false,
  user_data: null,
};

export const SignupProvider = ({ children }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [currentStep, setCurrentStep] = useState('/promoter-signup/phone');
  const [errors, setErrors] = useState({});
  const history = useHistory();

  // Load persisted data on mount
  useEffect(() => {
    const savedData = storage.get(STORAGE_KEY);
    if (savedData) {
      setFormData(savedData.formData || initialFormData);
      setCurrentStep(savedData.currentStep || '/promoter-signup/phone');
    }
  }, []);

  // Persist data on change
  useEffect(() => {
    storage.set(STORAGE_KEY, {
      formData,
      currentStep,
    });
  }, [formData, currentStep]);

  // Update form field
  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Update nested field (for addresses)
  const updateNestedField = (parent, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
    // Clear error
    const errorKey = `${parent}.${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Set multiple fields at once
  const updateFields = (fields) => {
    setFormData((prev) => ({
      ...prev,
      ...fields,
    }));
  };

  // Validation helpers
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    return phone && phone.length >= 10;
  };

  const validateRequired = (value) => {
    return value && value.toString().trim() !== '';
  };

  const validateAddress = (address, allowEmpty = false) => {
    if (allowEmpty && address.same_as_track) return true;
    if (allowEmpty && address.same_as_shipping) return true;

    const required = ['street1', 'city', 'state', 'zipcode'];
    return required.every((field) => validateRequired(address[field]));
  };

  // Step-specific validation
  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case '/promoter-signup/phone':
        if (!validatePhone(formData.phone)) {
          newErrors.phone = 'Please enter a valid phone number';
        }
        break;

      case '/promoter-signup/verify':
        if (
          !formData.verification_code ||
          formData.verification_code.length !== 6
        ) {
          newErrors.verification_code = 'Please enter the 6-digit code';
        }
        break;

      case '/promoter-signup/user-info':
        if (!validateRequired(formData.first_name)) {
          newErrors.first_name = 'First name is required';
        }
        if (!validateRequired(formData.last_name)) {
          newErrors.last_name = 'Last name is required';
        }
        if (!validateEmail(formData.email)) {
          newErrors.email = 'Please enter a valid email';
        }
        if (!validateRequired(formData.zipcode)) {
          newErrors.zipcode = 'Zip code is required';
        }
        break;

      case '/promoter-signup/business-details':
        if (!validateRequired(formData.promoter_type)) {
          newErrors.promoter_type = 'Promoter type is required';
        }
        if (!validateRequired(formData.business_dba)) {
          newErrors.business_dba = 'Track or series name is required';
        }
        if (!validateRequired(formData.legal_name)) {
          newErrors.legal_name = 'Legal business entity name is required';
        }
        if (!validateEmail(formData.business_email)) {
          newErrors.business_email = 'Please enter a valid email';
        }
        if (!validatePhone(formData.business_phone)) {
          newErrors.business_phone = 'Please enter a valid phone number';
        }
        // Conditional account owner validation
        if (formData.is_primary_owner === false) {
          if (!validateRequired(formData.account_owner?.first_name)) {
            newErrors['account_owner.first_name'] = 'First name is required';
          }
          if (!validateRequired(formData.account_owner?.last_name)) {
            newErrors['account_owner.last_name'] = 'Last name is required';
          }
          if (!validateEmail(formData.account_owner?.email)) {
            newErrors['account_owner.email'] = 'Valid email is required';
          }
          if (!validatePhone(formData.account_owner?.cellphone)) {
            newErrors['account_owner.cellphone'] = 'Valid phone is required';
          }
        }
        // Conditional payouts contact validation
        if (formData.has_separate_payouts_contact) {
          if (!validateRequired(formData.payouts_contact?.first_name)) {
            newErrors['payouts_contact.first_name'] = 'First name is required';
          }
          if (!validateRequired(formData.payouts_contact?.last_name)) {
            newErrors['payouts_contact.last_name'] = 'Last name is required';
          }
          if (!validateEmail(formData.payouts_contact?.email)) {
            newErrors['payouts_contact.email'] = 'Valid email is required';
          }
          if (!validatePhone(formData.payouts_contact?.cellphone)) {
            newErrors['payouts_contact.cellphone'] = 'Valid phone is required';
          }
        }
        break;

      case '/promoter-signup/track-address':
        if (!validateAddress(formData.track_address)) {
          newErrors.track_address =
            'Please complete all required address fields';
        }
        break;

      case '/promoter-signup/shipping-address':
        if (!validateAddress(formData.shipping_address, true)) {
          newErrors.shipping_address =
            'Please complete all required address fields';
        }
        break;

      case '/promoter-signup/mailing-address':
        if (!validateAddress(formData.mailing_address, true)) {
          newErrors.mailing_address =
            'Please complete all required address fields';
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation helpers
  const goToStep = (step) => {
    setCurrentStep(step);
    history.push(step);
  };

  const nextStep = (step) => {
    if (validateStep(currentStep)) {
      goToStep(step);
    }
  };

  const previousStep = (step) => {
    goToStep(step);
  };

  // Clear all data (after successful completion)
  const clearData = () => {
    setFormData(initialFormData);
    setCurrentStep('/promoter-signup/phone');
    setErrors({});
    storage.removeItem(STORAGE_KEY);
  };

  // Mark user as existing (skip user info step)
  const setExistingUser = (userData) => {
    setFormData((prev) => ({
      ...prev,
      is_existing_user: true,
      user_data: userData,
      first_name: userData.first_name || '',
      last_name: userData.last_name || '',
      email: userData.email || '',
    }));
  };

  // Get total steps based on user type and promoter type
  const getTotalSteps = () => {
    // Base steps: welcome, business-details, shipping, mailing, terms, review
    let totalSteps = 6;

    // Add user-info step if not existing user
    if (!formData.is_existing_user) {
      totalSteps += 1;
    }

    // Add track-address step if track promoter
    if (formData.promoter_type === 'track') {
      totalSteps += 1;
    }

    return totalSteps;
  };

  // Get current step number for a given route
  const getStepNumber = (route) => {
    // Build step order dynamically based on user type and promoter type
    const steps = ['/promoter-signup/welcome'];

    // Add user-info for new users
    if (!formData.is_existing_user) {
      steps.push('/promoter-signup/user-info');
    }

    // Always add business-details
    steps.push('/promoter-signup/business-details');

    // Add track-address only for track promoters
    if (formData.promoter_type === 'track') {
      steps.push('/promoter-signup/track-address');
    }

    // Add remaining steps
    steps.push(
      '/promoter-signup/shipping-address',
      '/promoter-signup/mailing-address',
      '/promoter-signup/terms',
      '/promoter-signup/review'
    );

    const index = steps.indexOf(route);

    // Return step number (1-indexed), or 0 if not found
    return index >= 0 ? index + 1 : 0;
  };

  const value = {
    formData,
    currentStep,
    errors,
    updateField,
    updateNestedField,
    updateFields,
    validateStep,
    nextStep,
    previousStep,
    goToStep,
    clearData,
    setExistingUser,
    setErrors,
    getTotalSteps,
    getStepNumber,
  };

  return (
    <SignupContext.Provider value={value}>{children}</SignupContext.Provider>
  );
};

export const useSignup = () => {
  const context = useContext(SignupContext);
  if (!context) {
    throw new Error('useSignup must be used within a SignupProvider');
  }
  return context;
};

export default SignupContext;
